import * as React from "react";
import { Button, Card } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { inputToAwsProvider, inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { Mk8sCluster } from "../../../../../schema/types/mk8s/entity/mk8s";
import { getDefaultAwsInput } from "../../../draft/utils/utils";
import { AwsSsmIcon } from "../../../awsSsmIcon";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpRadioGroup } from "../../../../gvc/testNewForm/CpRadioGroup";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemTaint } from "./CpArrayItemTaint";
import { store } from "../../../../../store";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";

import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpSwitch } from "../../../../gvc/testNewForm/CpSwitch";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { NodePoolCoreButton } from "../nodePoolCoreButton";
import { NodePoolDetectedTags } from "../detectedTags";

interface Props {
  index: number;
}
export const AwsNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const [recommendedAmis, setRecommendedAmis] = React.useState([]);
  React.useEffect(() => {
    fetchRecommendedAmis();
  }, []);
  async function fetchRecommendedAmis() {
    try {
      const cluster: Mk8sCluster = {
        name: "discover",
        spec: {
          provider: {
            aws: inputToAwsProvider(getDefaultAwsInput()),
          },
          version: "1.35.3",
        },
      } as any;
      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "recommended-amis",
          cluster: cluster,
        },
      });
      const res = data.recommended.map((r: any) => ({ label: r.name, value: r.name }));
      setRecommendedAmis(res);
    } catch (e) {
      setRecommendedAmis([]);
    }
  }

  const region = useCpFormValue(`aws.region`);
  const name = useCpFormValue(`aws.nodePools.${index}.name`);
  const imageType = useCpFormValue(`aws.nodePools.${index}.overrideImage.type`);
  const instanceTypes = useCpFormValue(`aws.nodePools.${index}.instanceTypes`);
  const subnetIds = useCpFormValue(`aws.nodePools.${index}.subnetIds`);
  const extraSecurityGroupIds = useCpFormValue(`aws.nodePools.${index}.extraSecurityGroupIds`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={["aws.nodePools"]} />
          </CardTitle>
          <CardContent>
            <div>
              <NodePoolDetectedTags name={`aws.nodePools.${index}.labels`} />
              <CpFormItem>
                <CpLabel name={`aws.nodePools.${index}.name`}>Name</CpLabel>
                <CpInput name={`aws.nodePools.${index}.name`} style={{ maxWidth: 600 }} />
                <CpErrorWarning name={`aws.nodePools.${index}.name`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex gap-2">
                  <div className="flex flex-col">
                    <CpLabel name={`aws.nodePools.${index}.minSize`}>Min Size</CpLabel>
                    <CpInputNumber name={`aws.nodePools.${index}.minSize`} />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`aws.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                    <CpInputNumber name={`aws.nodePools.${index}.maxSize`} />
                  </div>
                </div>
                <CpErrorWarningArray names={[`aws.nodePools.${index}.minSize`, `aws.nodePools.${index}.maxSize`]} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name="skip">Override Image</CpLabel>
                <CpRadioGroup
                  name={`aws.nodePools.${index}.overrideImage.type`}
                  style={{ width: 450 }}
                  options={[
                    { label: "None", value: "none" },
                    { label: "Exact", value: "exact" },
                    { label: "Recommended", value: "recommended" },
                  ]}
                />
                {imageType === "exact" ? (
                  <>
                    <CpInput name={`aws.nodePools.${index}.overrideImage.exact`} style={{ width: 450 }} />
                    <CpErrorWarning name={`aws.nodePools.${index}.overrideImage.exact`} />
                  </>
                ) : imageType === "recommended" ? (
                  <>
                    <CpSelect
                      name={`aws.nodePools.${index}.overrideImage.recommended`}
                      style={{ width: 450 }}
                      options={recommendedAmis}
                    />
                    <CpErrorWarning name={`aws.nodePools.${index}.overrideImage.recommended`} />
                  </>
                ) : (
                  <>
                    <CpErrorWarning name={`aws.nodePools.${index}.overrideImage.type`} />
                  </>
                )}
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`aws.nodePools.${index}.bootDiskSize`}>Boot Disk Size</CpLabel>
                <CpInputNumber name={`aws.nodePools.${index}.bootDiskSize`} placeholder="20" addonAfter={`GB`} />
                <CpErrorWarning name={`aws.nodePools.${index}.bootDiskSize`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex items-center gap-2">
                  <CpLabel name={`aws.nodePools.${index}.cpuOptions.nestedVirtualization`}>
                    CPU Options | Nested Virtualization
                  </CpLabel>
                  <CpSwitch name={`aws.nodePools.${index}.cpuOptions.nestedVirtualization`} />
                </div>
                <CpErrorWarning name={`aws.nodePools.${index}.cpuOptions.nestedVirtualization`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`aws.nodePools.${index}.onDemandBaseCapacity`}>On Demand Base Capacity</CpLabel>
                <CpInputNumber name={`aws.nodePools.${index}.onDemandBaseCapacity`} />
                <CpErrorWarning name={`aws.nodePools.${index}.onDemandBaseCapacity`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`aws.nodePools.${index}.onDemandPercentageAboveBaseCapacity`}>
                  On Demand Percentage Above Base Capacity
                </CpLabel>
                <CpInputNumber name={`aws.nodePools.${index}.onDemandPercentageAboveBaseCapacity`} />
                <CpErrorWarning name={`aws.nodePools.${index}.onDemandPercentageAboveBaseCapacity`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`aws.nodePools.${index}.spotAllocationStrategy`}>Spot Allocation Strategy</CpLabel>
                <CpSelect
                  name={`aws.nodePools.${index}.spotAllocationStrategy`}
                  options={[
                    { label: "lowest-price", value: "lowest-price" },
                    { label: "capacity-optimized", value: "capacity-optimized" },
                    { label: "capacity-optimized-prioritized", value: "capacity-optimized-prioritized" },
                    { label: "price-capacity-optimized", value: "price-capacity-optimized" },
                  ]}
                  style={{ maxWidth: 600 }}
                />
                <CpErrorWarning name={`aws.nodePools.${index}.spotAllocationStrategy`} />
              </CpFormItem>
              <div style={{ maxWidth: 600 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Instance Types</CpLabel>
                    <Button
                      className="ml-1"
                      size={"small"}
                      type={"default"}
                      onClick={() => setBrowserKey("instance-types")}
                    >
                      Browse
                    </Button>
                  </div>
                  <div className="-mt-2">
                    <CpArrayString name={`aws.nodePools.${index}.instanceTypes`} />
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 600 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Subnet IDs</CpLabel>
                    <AwsSsmIcon />
                    <Button
                      className="ml-1"
                      size={"small"}
                      type={"default"}
                      onClick={() => setBrowserKey("subnet-ids")}
                    >
                      Browse
                    </Button>
                  </div>
                  <div className="-mt-2">
                    <CpArrayString name={`aws.nodePools.${index}.subnetIds`} />
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 600 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Extra Security Group IDs</CpLabel>
                    <AwsSsmIcon />
                    <Button
                      className="ml-1"
                      size={"small"}
                      type={"default"}
                      onClick={() => setBrowserKey("extra-security-group-ids")}
                    >
                      Browse
                    </Button>
                  </div>
                  <div className="-mt-2">
                    <CpArrayString name={`aws.nodePools.${index}.extraSecurityGroupIds`} />
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Labels</CpLabel>
                    <NodePoolCoreButton name={`aws.nodePools.${index}.labels`} />
                  </div>
                  <div className="-mt-2">
                    <CpArrayObject
                      factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                      labels={["Name", "Value"]}
                      name={`aws.nodePools.${index}.labels`}
                    >
                      <CpArrayItemNameValuePair />
                    </CpArrayObject>
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                  labels={["Key", "Value", "Effect"]}
                  label="Taints"
                  name={`aws.nodePools.${index}.taints`}
                >
                  <CpArrayItemTaint />
                </CpArrayObject>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "instance-types" ? (
        <NGProviderBrowser
          width={1550}
          title="Browse AWS Instance Types"
          initialValue={instanceTypes.map((i) => i.value)}
          filteredColumns={["Name"]}
          distinctFilters={["CPU Type", "Cores", "Memory", "Hypervisor", "Arch", "GPU Type", "GPU Memory", "Storage"]}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: `instance-types-${region}`,
                cluster: inputToMk8s(values),
              },
            });
            let instanceTypes = data.data;
            instanceTypes = instanceTypes.sort((a: any, b: any) => {
              return a.price - b.price || a.cores - b.cores || a.memory - b.memory || a.instanceType - b.instanceType;
            });
            return instanceTypes;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[formId];
            const formValues: Mk8sInput = form.values as any;
            const existing = formValues.aws.nodePools[index].instanceTypes;

            const newItems = values.map((value) => {
              const found = existing.find((i) => i.value === value);
              return found || { id: uuidv4(), value };
            });
            formSetValue(formId, `aws.nodePools.${index}.instanceTypes`, newItems);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          initialSort={["Cost per Month", "asc"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          labels={[
            "Name",
            "Cost per Month",
            "CPU Type",
            "Cores",
            "Memory",
            "Arch",
            "Storage",
            "GPU Type",
            "GPU Memory",
            "Hypervisor",
          ]}
          sortable
          getData={(item: any) => {
            return [
              item.instanceType,
              item.instanceType,
              [item.price, `$${item.price.toFixed(2)}`],
              item.cpuType,
              item.cores,
              [item.memory, formatSizeForProviderBrowser(item.memory)],
              item.arch,
              item.storage,
              [item.gpuType, item.gpuType || "-"],
              [item.gpuMemory, item.gpuMemory ? item.gpuMemory : "-"],
              item.hypervisor,
            ];
          }}
        />
      ) : null}
      {browserKey === "subnet-ids" ? (
        <NGProviderBrowser
          title="Browse AWS Subnets"
          initialValue={subnetIds.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ec2-subnets",
                cluster: inputToMk8s(values),
              },
            });
            return data.Subnets;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[formId];
            const formValues: Mk8sInput = form.values as any;
            const existing = formValues.aws.nodePools[index].subnetIds;

            const newItems = values.map((value) => {
              const found = existing.find((i) => i.value === value);
              return found || { id: uuidv4(), value };
            });
            formSetValue(formId, `aws.nodePools.${index}.subnetIds`, newItems);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Subnet ID", "Availability Zone", "CIDR"]}
          getData={(item: any) => {
            let name = "-";
            const nameTag = (item.Tags || []).find((t: any) => t.Key === "Name");
            if (nameTag) {
              name = nameTag.Value;
            }
            return [item.SubnetId, name, item.SubnetId, item.AvailabilityZone, item.CidrBlock];
          }}
        />
      ) : null}
      {browserKey === "extra-security-group-ids" ? (
        <NGProviderBrowser
          title="Browse AWS Security Groups"
          initialValue={extraSecurityGroupIds.map((i) => i.value)}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "ec2-securityGroups",
                cluster: inputToMk8s(values),
              },
            });
            return data.SecurityGroups;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            const form = store.getState().form.instances[formId];
            const formValues: Mk8sInput = form.values as any;
            const existing = formValues.aws.nodePools[index].extraSecurityGroupIds;

            const newItems = values.map((value) => {
              const found = existing.find((i) => i.value === value);
              return found || { id: uuidv4(), value };
            });
            formSetValue(formId, `aws.nodePools.${index}.extraSecurityGroupIds`, newItems);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Description", "Security Group ID"]}
          getData={(item: any) => [item.GroupId, item.GroupName, item.Description, item.GroupId]}
        />
      ) : null}
    </>
  );
};
